import { useCallback, useEffect, useState } from 'react'
import { getDictByKey } from '../api/dictionaryService'
import { Toast } from 'antd-mobile'
import { checkSupportWxpay, checkSupportWxpayWithLinkId } from '../api/orderService'

interface PayChannel {
  key: string
  router: string
  weight: number
  appId?: string
  allowedDomains?: string[]
}

interface PaymentType {
  type: string
  title: string
  image: string
  desc?: string
  channels: PayChannel[]
}

export interface ChannelOption {
  key: string
  title: string
  image: string
  router: string
  desc?: string
  appId?: string
}

interface UsePaymentChannelsProps {
  orderId?: string
  linkId?: string
}

const isChannelSupportedForDomain = (channel: PayChannel): boolean => {
  if (!channel.allowedDomains || channel.allowedDomains.length === 0) {
    return true
  }
  const currentDomain = window.location.host
  return channel.allowedDomains.includes(currentDomain)
}

const selectChannelByWeight = (channels: PayChannel[]): PayChannel | null => {
  const supportedChannels = channels.filter(isChannelSupportedForDomain).filter((c) => (c.weight || 0) > 0)

  if (supportedChannels.length === 0) {
    return null
  }

  const totalWeight = supportedChannels.reduce((sum, channel) => sum + channel.weight, 0)

  let random = Math.random() * totalWeight

  for (const channel of supportedChannels) {
    random -= channel.weight
    if (random <= 0) {
      return channel
    }
  }

  return supportedChannels[0]
}

export const usePaymentChannels = ({ orderId, linkId }: UsePaymentChannelsProps = {}) => {
  const [channels, setChannels] = useState<ChannelOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>('')

  const getAvailableChannels = useCallback(
    async (paymentTypes: PaymentType[]): Promise<ChannelOption[]> => {
      const availableChannels: ChannelOption[] = []

      for (const paymentType of paymentTypes) {
        if (paymentType.type === 'wxpay') {
          try {
            let isSupported = '1'
            if (orderId) {
              isSupported = await checkSupportWxpay({ orderId })
            } else if (linkId) {
              isSupported = await checkSupportWxpayWithLinkId({ linkId })
            }
            if (isSupported !== '1') {
              continue
            }
          } catch {
            continue
          }
        }

        const selectedChannel = selectChannelByWeight(paymentType.channels)
        if (selectedChannel) {
          availableChannels.push({
            key: selectedChannel.key,
            title: paymentType.title,
            image: paymentType.image,
            router: selectedChannel.router,
            desc: paymentType.desc,
            appId: selectedChannel.appId,
          })
        }
      }

      return availableChannels
    },
    [orderId, linkId]
  )

  useEffect(() => {
    const fetchChannels = async () => {
      try {
        setLoading(true)
        const data = await getDictByKey('key_pay_channel')
        const paymentTypes: PaymentType[] = JSON.parse(data)
        const availableChannels = await getAvailableChannels(paymentTypes)
        setChannels(availableChannels)
        setError('')
      } catch (err) {
        setError('获取支付渠道失败')
        Toast.show('获取支付渠道失败')
        setChannels([])
      } finally {
        setLoading(false)
      }
    }

    fetchChannels().then()
  }, [getAvailableChannels])

  const getRoutersString = (): string => {
    return channels.map((channel) => channel.router).join(',')
  }

  return {
    channels,
    paymentLoading: loading,
    error,
    getRoutersString,
  }
}
